import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import BackendService from 'BackendService';
import { Action, ActionType } from '../../models/Action';
import type { ActionsContextType } from '../../types/ActionsContextType';

export type { ActionsContextType };

const ActionsContext = createContext<ActionsContextType | undefined>(undefined);

interface ActionsProviderProps {
  children: ReactNode;
}

export const ActionsProvider: React.FC<ActionsProviderProps> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [unresolvedActions, setUnresolvedActions] = useState<Action[] | null>(null);

  const refreshActions = async () => {
    try {
      setLoading(true);
      const backendService = new BackendService();
      const actions = await backendService.getOutstandingActions();
      setUnresolvedActions(actions);
    } catch (error) {
      console.error('Failed to fetch actions data:', error);
      setUnresolvedActions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshActions();
  }, []);

  // Check for new actions every 15 minutes
  useEffect(() => {
    const interval = setInterval(
      () => {
        console.log('Checking for new actions...');
        refreshActions();
      },
      15 * 60 * 1000,
    ); // 15 minutes in milliseconds

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, []);

  const getActionsByType = (types?: ActionType[]) => {
    if (!unresolvedActions) return null;
    if (!types || types.length === 0) return unresolvedActions;
    return unresolvedActions.filter((a) => types.includes(a.actionType));
  };

  const contextValue: ActionsContextType = {
    unresolvedActions: (types?: ActionType[]) => getActionsByType(types),
    loading: () => loading,
    refreshActions,
  };

  console.log('Actions context state:', {
    loading,
    unresolvedActions: unresolvedActions?.length || 0,
  });

  return <ActionsContext.Provider value={contextValue}>{children}</ActionsContext.Provider>;
};

export const useActions = (): ActionsContextType => {
  const context = useContext(ActionsContext);
  if (!context) {
    throw new Error('useActions must be used within an ActionsProvider');
  }
  return context;
};

export default ActionsContext;
